import { useState } from "react";
import { Building2, GraduationCap, CheckCircle2, Loader2, Lock } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import { createOrganization } from "../../api/services/platformAdminService";

const TABS = [
  {
    key: "company",
    label: "Company",
    icon: Building2,
    fieldLabel: "Company Name",
    placeholder: "Enter company name",
    description: "Register a new company organization to the platform.",
  },
  {
    key: "university",
    label: "University",
    icon: GraduationCap,
    fieldLabel: "University Name",
    placeholder: "Enter university name",
    description: "Register a new university organization to the platform.",
    locked: true,
  },
];

const inputClass =
  "w-full rounded-lg border border-(--color-border) bg-(--color-bg)/50 px-4 py-2.5 text-sm text-(--color-text-primary) placeholder:text-(--color-text-muted) focus:outline-none focus:border-(--color-primary) focus:shadow-[0_0_0_3px_var(--color-primary-100)] transition-all";

export default function RegisterOrganizationsPage() {
  const [activeTab, setActiveTab] = useState("company");
  const [values, setValues] = useState({ company: "", university: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  const currentTab = TABS.find((t) => t.key === activeTab);
  const TabIcon = currentTab.icon;

  const handleRegister = async () => {
    const value = values[activeTab].trim();
    if (!value) {
      setError(`${currentTab.fieldLabel} is required.`);
      return;
    }

    setError("");
    setLoading(true);

    try {
      if (activeTab === "company") {
        await createOrganization({ name: value, orgType: "company" });
      } else {
        await new Promise((r) => setTimeout(r, 1200));
      }

      setSuccess(activeTab);
      setValues((prev) => ({ ...prev, [activeTab]: "" }));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const detail = err?.payload?.detail;
      const message =
        (typeof detail === "string" ? detail : detail?.message) ||
        err?.payload?.message ||
        err?.message ||
        "Unable to register organization. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="px-8 py-8 space-y-6 min-h-screen">
        {/* Page header — mirrors RegisterAdmins */}
        <div>
          <h1 className="text-2xl font-bold text-(--color-text-primary)">
            Register Organizations
          </h1>
          <p className="text-xs mt-0.5 text-(--color-text-muted)">
            Add companies or universities to the platform.
          </p>
        </div>

        {/* Card */}
        <div className="rounded-xl bg-(--color-bg)/30 dark:bg-(--color-bg-card) border border-(--color-border)">
          {/* Tab bar */}
          <div className="flex border-b border-(--color-border)">
            {TABS.map(({ key, label, icon: Icon, locked }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={locked}
                  onClick={() => {
                    if (locked) return;
                    setActiveTab(key);
                    setError("");
                    setSuccess(null);
                  }}
                  title={locked ? `${label} (coming soon)` : label}
                  className={`
        relative flex-1 flex items-center justify-center gap-2
        px-4 py-3 text-sm font-medium transition-colors duration-150
        ${
          locked
            ? "text-(--color-text-muted) opacity-50 cursor-not-allowed"
            : isActive
              ? "text-(--color-primary)"
              : "text-(--color-text-muted) hover:text-(--color-text-primary)"
        }
      `}
                >
                  <Icon size={15} />
                  {label}
                  {locked && <Lock size={12} className="shrink-0" />}
                  {isActive && !locked && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-(--color-primary) rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Card body */}
          <div className="px-6 py-6 space-y-6">
            {/* Field */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-(--color-text-primary)">
                {currentTab.fieldLabel}
              </label>
              <input
                type="text"
                value={values[activeTab]}
                onChange={(e) => {
                  setValues((prev) => ({
                    ...prev,
                    [activeTab]: e.target.value,
                  }));
                  if (error) setError("");
                }}
                onKeyDown={(e) =>
                  e.key === "Enter" && !loading && handleRegister()
                }
                placeholder={currentTab.placeholder}
                className={`${inputClass} ${error ? "border-red-500 focus:border-red-500" : ""}`}
              />
              {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
            </div>

            <p className="text-sm text-(--color-text-muted)">
              {currentTab.description}
            </p>

            {/* Success banner */}
            {success === activeTab && (
              <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
                <CheckCircle2 size={16} className="shrink-0" />
                <span>
                  <span className="font-semibold capitalize">{success}</span>{" "}
                  organization registered successfully.
                </span>
              </div>
            )}

            {/* Submit — right-aligned, matches RegisterAdmins */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={handleRegister}
                disabled={loading}
                className="px-6 py-2.5 rounded-lg text-sm font-medium bg-(--color-primary) text-(--color-text-on-primary) shadow-[0_0_10px_var(--color-shadow-primary)] hover:shadow-[0_0_16px_var(--color-shadow-primary-hover),0_0_32px_var(--color-shadow-primary)] transition-shadow duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Registering…
                  </>
                ) : (
                  <>
                    <TabIcon size={15} />
                    Register {currentTab.label}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
